import React from 'react';

// Sans frontière d'erreur, la moindre exception pendant un rendu démonte tout
// l'arbre React : l'utilisateur se retrouve devant une page blanche, sans message
// ni moyen de repartir. Ce composant intercepte l'erreur, l'affiche, et offre les
// deux seules issues utiles ici : recharger, ou repartir des données de démo
// (utile quand un état persisté incohérent provoque la panne à chaque rechargement).

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // En production, remplacer par l'envoi vers un collecteur (Sentry, GCP Error
    // Reporting...). La console reste le seul canal disponible pour l'instant.
    console.error('Erreur non rattrapée dans le rendu React :', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleResetData = () => {
    const confirmed = window.confirm(
      "Réinitialiser toutes les données locales ? À utiliser si l'erreur revient à chaque rechargement."
    );
    if (!confirmed) return;
    try {
      window.localStorage.clear();
    } catch {
      /* stockage indisponible : le rechargement reste utile */
    }
    window.location.href = window.location.pathname;
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6 font-sans">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl shadow-xl p-8 space-y-5">
          <div className="space-y-2">
            <span className="text-[11px] font-black uppercase tracking-widest text-[#D95D39]">
              feed-it
            </span>
            <h1 className="text-xl font-black tracking-tight text-slate-900">
              Une erreur inattendue est survenue
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              L'application n'a pas pu afficher cette page. Vos données enregistrées sur cet
              appareil n'ont pas été modifiées.
            </p>
          </div>

          <details className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <summary className="text-xs font-bold text-slate-700 cursor-pointer">
              Détail technique
            </summary>
            <pre className="mt-2 text-[11px] text-slate-600 whitespace-pre-wrap break-words font-mono">
              {error.message || String(error)}
            </pre>
          </details>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={this.handleReload}
              className="px-4 py-2.5 bg-[#D95D39] hover:bg-orange-600 text-white text-xs font-black uppercase tracking-wider rounded-xl cursor-pointer transition-colors"
            >
              Recharger la page
            </button>
            <button
              onClick={this.handleResetData}
              className="px-4 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl cursor-pointer transition-colors"
            >
              Réinitialiser les données locales
            </button>
          </div>
        </div>
      </div>
    );
  }
}
